import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Assignment, Task } from '@app/shared'

import { data, dayTriageKey } from '../../lib/data'
import type { MailCorrectionReason, MailProposal, MailTriage, MailTriageItem, MailTriageQuery, MailVerdict } from '../../lib/data/types'
import {
  DAY_COLLAPSE_MS,
  DAY_DISMISS_GONE_AT,
  DAY_DISMISS_LEAVE_AT,
  DAY_DONE_GONE_AT,
  DAY_DONE_LEAVE_AT,
  DAY_DONE_STRIKE_AT,
  DAY_EDGE_MS,
  DAY_MOVE_GONE_AT,
  DAY_WASH_MS,
  DUR_STATE,
  UNDO_WINDOW_MS,
  prefersReducedMotion,
} from '../../lib/motion'
import { PILE_NAME, REASON_CHIPS } from './designed'
import { acceptPlan, patchProposal } from './proposals'

// ============================================================================
// THE DAY PAGE'S ACTIONS — Done, Dismiss, Move, Snooze, and the 5-second undo.
//
// THE UNDO CONTRACT (owner, closed): every action applies OPTIMISTICALLY — the row starts leaving
// on the click and the write goes out at once. The toast offers Undo for 5 s; Undo calls the
// INVERSE (`state: 'open'`, or the previous verdict) and puts the row back where it was. When the
// toast expires nothing else is sent. A reason chip in the toast sends a second verdict call with
// `reason`. Nothing here ever writes to Gmail — none of these endpoints can.
//
// THE MOTION (14a–14d) RUNS ON PHASES, not on the data. A row's phase walks rest → (checking →
// striking | dim) → leaving → gone on the MotionSpec's beats; the cache is patched at `gone`, so
// the counts roll as the row closes, and the row is kept as a "ghost" in its section for the
// collapse. Under reduced motion every beat is 0 ms and the end state is simply there — except the
// undo window, which is a deadline.
// ============================================================================

export type Section = 'needs' | 'waiting' | 'important' | 'unsorted' | 'snoozed' | 'can_wait' | 'noise'

export type Phase = 'rest' | 'checking' | 'striking' | 'dim' | 'leaving' | 'gone' | 'pre-return' | 'returning'

export interface DayItem {
  /** `mail:<id>` · `assignment:<id>` · `reminder:<id>`. */
  key: string
  kind: 'mail' | 'assignment' | 'reminder'
  title: string
  /** The real thing: the Gmail thread, the bCourses page. Null when there is none. */
  url: string | null
  mail?: MailTriageItem
  task?: Task
  assignment?: Assignment
}

interface PhaseEntry {
  phase: Phase
  mark?: 'done' | 'dismiss'
  section: Section
  item: DayItem
  index: number
}

export interface ToastState {
  id: number
  verb: string
  title: string
  icon: 'done' | 'dismiss' | 'move' | 'snooze' | 'error'
  /** Mail moves offer the five reason chips. */
  chips: { id: string; verdict: MailVerdict } | null
  noted: string | null
  undo: (() => void) | null
  on: boolean
}

export interface HandledRow {
  key: string
  verb: 'Done' | 'Dismissed'
  title: string
  at: string
  undo: () => void
}

export interface MenuState {
  key: string
  kind: 'snooze' | 'picker' | 'rules'
}

const MAIL_GROUP: Record<MailVerdict, keyof MailTriage['groups']> = {
  needs_you_today: 'needsYouToday',
  important: 'important',
  can_wait: 'canWait',
  noise: 'noise',
}
const MAIL_COUNT: Record<MailVerdict, keyof MailTriage['counts']> = {
  needs_you_today: 'needsYouToday',
  important: 'important',
  can_wait: 'canWait',
  noise: 'noise',
}

function errorText(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'That did not go through. Nothing changed.'
}

// ── Pure cache patches (exported for the tests) ─────────────────────────────

/** Take one message off whatever pile it is in. */
export function withoutMail(t: MailTriage, id: string): { next: MailTriage; item: MailTriageItem | null; from: keyof MailTriage['groups'] | null } {
  const groups = { ...t.groups }
  const counts = { ...t.counts }
  let item: MailTriageItem | null = null
  let from: keyof MailTriage['groups'] | null = null
  for (const g of Object.keys(groups) as (keyof MailTriage['groups'])[]) {
    const list = groups[g]
    if (!list) continue
    const at = list.findIndex((m) => m.id === id)
    if (at === -1) continue
    item = list[at]!
    from = g
    groups[g] = [...list.slice(0, at), ...list.slice(at + 1)]
    const countKey = g as keyof MailTriage['counts']
    if (typeof counts[countKey] === 'number') (counts[countKey] as number) = Math.max(0, (counts[countKey] as number) - 1)
  }
  return { next: { ...t, groups, counts }, item, from }
}

export function patchHandled(t: MailTriage, id: string): MailTriage {
  const { next } = withoutMail(t, id)
  return { ...next, counts: { ...next.counts, handledToday: (next.counts.handledToday ?? 0) + 1 } }
}

export function patchMove(t: MailTriage, id: string, verdict: MailVerdict): MailTriage {
  const { next, item } = withoutMail(t, id)
  if (!item) return t
  const moved: MailTriageItem = { ...item, verdict, ownerVerdict: verdict }
  const g = MAIL_GROUP[verdict]
  const list = [...(next.groups[g] ?? []), moved].sort((a, b) => Date.parse(b.receivedAt) - Date.parse(a.receivedAt))
  return {
    ...next,
    groups: { ...next.groups, [g]: list },
    counts: { ...next.counts, [MAIL_COUNT[verdict]]: (next.counts[MAIL_COUNT[verdict]] as number) + 1 },
  }
}

export function patchSnooze(t: MailTriage, id: string, until: string): MailTriage {
  const { next, item } = withoutMail(t, id)
  if (!item) return t
  const snoozed = [...(next.groups.snoozed ?? []), { ...item, state: 'snoozed' as const, snoozedUntil: until }].sort(
    (a, b) => Date.parse(a.snoozedUntil!) - Date.parse(b.snoozedUntil!),
  )
  return {
    ...next,
    groups: { ...next.groups, snoozed },
    counts: { ...next.counts, snoozed: (next.counts.snoozed ?? 0) + 1 },
    nextSnoozedAt: snoozed[0]?.snoozedUntil ?? null,
  }
}

// ── The hook ────────────────────────────────────────────────────────────────

export function useDayController(query: MailTriageQuery, clockMinutes: () => number) {
  const qc = useQueryClient()
  const key = useMemo(() => dayTriageKey(query), [query])
  const [phases, setPhases] = useState<Record<string, PhaseEntry>>({})
  const [toast, setToast] = useState<ToastState | null>(null)
  const [handled, setHandled] = useState<HandledRow[]>([])
  const [hidden, setHidden] = useState<Set<string>>(() => new Set())
  const [flash, setFlash] = useState<string | null>(null)
  const [menu, setMenu] = useState<MenuState | null>(null)
  const [movedNow, setMovedNow] = useState<Set<string>>(() => new Set())
  /** The card whose Next steps panel is open (one at a time). */
  const [steps, setSteps] = useState<string | null>(null)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const toastSeq = useRef(0)

  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout)
      if (toastTimer.current) clearTimeout(toastTimer.current)
    },
    [],
  )

  /** A beat on the MotionSpec's clock — 0 ms under reduced motion. */
  const later = useCallback((ms: number, fn: () => void) => {
    const t = setTimeout(fn, prefersReducedMotion() ? 0 : ms)
    timers.current.push(t)
  }, [])

  const setPhase = useCallback((k: string, patch: Partial<PhaseEntry> | null) => {
    setPhases((cur) => {
      if (patch === null) {
        const { [k]: _gone, ...rest } = cur
        return rest
      }
      const prev = cur[k]
      if (!prev && !patch.item) return cur
      return { ...cur, [k]: { ...(prev as PhaseEntry), ...patch } }
    })
  }, [])

  const invalidate = useCallback(
    (kind: DayItem['kind']) => {
      if (kind === 'mail') {
        void qc.invalidateQueries({ queryKey: ['mailTriage'] })
        void qc.invalidateQueries({ queryKey: ['mailAudit'] })
      } else {
        void qc.invalidateQueries({ queryKey: ['tasks'] })
        void qc.invalidateQueries({ queryKey: ['reminders'] })
      }
    },
    [qc],
  )

  // ── The toast ──
  const hideToast = useCallback(() => {
    if (toastTimer.current) clearTimeout(toastTimer.current)
    setToast((t) => (t ? { ...t, on: false } : null))
    const t = setTimeout(() => setToast((cur) => (cur && !cur.on ? null : cur)), prefersReducedMotion() ? 0 : DUR_STATE)
    timers.current.push(t)
  }, [])

  const showToast = useCallback(
    (t: Omit<ToastState, 'id' | 'on' | 'noted'>) => {
      if (toastTimer.current) clearTimeout(toastTimer.current)
      const id = ++toastSeq.current
      setToast({ ...t, id, on: false, noted: null })
      requestAnimationFrame(() => setToast((cur) => (cur && cur.id === id ? { ...cur, on: true } : cur)))
      // THE WINDOW. On expiry nothing is sent — the toast simply goes.
      toastTimer.current = setTimeout(() => hideToast(), UNDO_WINDOW_MS)
    },
    [hideToast],
  )

  const fail = useCallback(
    (title: string, error: unknown) => {
      showToast({ verb: 'Not saved', title: `${title} — ${errorText(error)}`, icon: 'error', chips: null, undo: null })
    },
    [showToast],
  )

  /** The row grows back in place, with its source edge for a moment (14d). */
  const bringBack = useCallback(
    (entry: Omit<PhaseEntry, 'phase'>) => {
      setPhases((cur) => ({ ...cur, [entry.item.key]: { ...entry, phase: 'pre-return' } }))
      requestAnimationFrame(() => setPhase(entry.item.key, { phase: 'returning' }))
      later(DAY_EDGE_MS + DAY_COLLAPSE_MS, () => setPhase(entry.item.key, null))
    },
    [later, setPhase],
  )

  const snapshot = useCallback(() => qc.getQueryData<MailTriage>(key), [qc, key])
  const patch = useCallback(
    (fn: (t: MailTriage) => MailTriage) => qc.setQueryData<MailTriage>(key, (t) => (t ? fn(t) : t)),
    [qc, key],
  )

  /** The exit, on its beats. Returns when the row is gone. */
  const exit = useCallback(
    (item: DayItem, section: Section, index: number, kind: 'done' | 'dismiss' | 'move', onGone: () => void) => {
      const k = item.key
      if (kind === 'done') {
        setPhases((cur) => ({ ...cur, [k]: { phase: 'checking', mark: 'done', section, item, index } }))
        later(DAY_DONE_STRIKE_AT, () => setPhase(k, { phase: 'striking' }))
        later(DAY_DONE_LEAVE_AT, () => setPhase(k, { phase: 'leaving' }))
        later(DAY_DONE_GONE_AT, () => {
          setPhase(k, { phase: 'gone' })
          onGone()
        })
        later(DAY_DONE_GONE_AT + DAY_COLLAPSE_MS, () => setPhase(k, null))
      } else if (kind === 'dismiss') {
        setPhases((cur) => ({ ...cur, [k]: { phase: 'dim', mark: 'dismiss', section, item, index } }))
        later(DAY_DISMISS_LEAVE_AT, () => setPhase(k, { phase: 'leaving' }))
        later(DAY_DISMISS_GONE_AT, () => {
          setPhase(k, { phase: 'gone' })
          onGone()
        })
        later(DAY_DISMISS_GONE_AT + DAY_COLLAPSE_MS, () => setPhase(k, null))
      } else {
        setPhases((cur) => ({ ...cur, [k]: { phase: 'leaving', section, item, index } }))
        later(DAY_MOVE_GONE_AT, () => {
          setPhase(k, { phase: 'gone' })
          onGone()
        })
        later(DAY_MOVE_GONE_AT + DAY_COLLAPSE_MS, () => setPhase(k, null))
      }
    },
    [later, setPhase],
  )

  const busy = useCallback((k: string) => Boolean(phases[k] && phases[k]!.phase !== 'returning'), [phases])

  // ── Done / Dismiss ──
  const finish = useCallback(
    (item: DayItem, section: Section, index: number, how: 'done' | 'dismiss') => {
      if (busy(item.key)) return
      setMenu(null)
      const verb = how === 'done' ? 'Done' : 'Dismissed'
      if (item.kind === 'mail' && item.mail) {
        const id = item.mail.id
        const before = snapshot()
        let gone = false
        let wrote = false
        let failure: { e: unknown } | null = null
        const rollback = () => {
          if (before) qc.setQueryData(key, before)
          setHandled((h) => h.filter((r) => r.key !== item.key))
          bringBack({ item, section, index })
          fail(item.title, failure?.e)
        }
        data.setMailState(id, how === 'done' ? 'done' : 'dismissed').then(
          () => {
            wrote = true
            if (gone) invalidate('mail')
          },
          (e) => {
            failure = { e }
            if (gone) rollback()
          },
        )
        const undo = () => {
          hideToast()
          if (before) qc.setQueryData(key, before)
          setHandled((h) => h.filter((r) => r.key !== item.key))
          bringBack({ item, section, index })
          data.setMailState(id, 'open').then(() => invalidate('mail'), (e) => fail(item.title, e))
        }
        exit(item, section, index, how, () => {
          gone = true
          if (failure) return rollback()
          patch((t) => patchHandled(t, id))
          if (wrote) invalidate('mail')
          setHandled((h) => [{ key: item.key, verb, title: item.title, at: String(clockMinutes()), undo }, ...h.filter((r) => r.key !== item.key)])
          showToast({ verb, title: item.title, icon: how, chips: null, undo })
        })
        return
      }
      if (item.kind === 'reminder' && item.task && how === 'done') {
        const task = item.task
        let gone = false
        let failure: { e: unknown } | null = null
        const rollback = () => {
          setHidden((h) => {
            const n = new Set(h)
            n.delete(item.key)
            return n
          })
          setHandled((hs) => hs.filter((r) => r.key !== item.key))
          bringBack({ item, section, index })
          fail(item.title, failure?.e)
        }
        data.toggleTask(task.id, true).then(
          () => invalidate('reminder'),
          (e) => {
            failure = { e }
            if (gone) rollback()
          },
        )
        const undo = () => {
          hideToast()
          setHidden((h) => {
            const n = new Set(h)
            n.delete(item.key)
            return n
          })
          setHandled((hs) => hs.filter((r) => r.key !== item.key))
          bringBack({ item, section, index })
          data.toggleTask(task.id, false).then(() => invalidate('reminder'), (e) => fail(item.title, e))
        }
        exit(item, section, index, 'done', () => {
          gone = true
          if (failure) return rollback()
          setHidden((h) => new Set(h).add(item.key))
          setHandled((h) => [{ key: item.key, verb, title: item.title, at: String(clockMinutes()), undo }, ...h])
          showToast({ verb, title: item.title, icon: 'done', chips: null, undo })
        })
      }
    },
    [busy, snapshot, invalidate, qc, key, bringBack, fail, hideToast, exit, patch, clockMinutes, showToast],
  )

  // ── Move (mail only) ──
  const move = useCallback(
    (item: DayItem, section: Section, index: number, verdict: MailVerdict) => {
      const mail = item.mail
      if (!mail || busy(item.key) || mail.verdict === verdict) return
      setMenu(null)
      const id = mail.id
      const previous = mail.verdict
      const before = snapshot()
      let gone = false
      let wrote = false
      let failure: { e: unknown } | null = null
      const rollback = () => {
        if (before) qc.setQueryData(key, before)
        bringBack({ item, section, index })
        fail(item.title, failure?.e)
      }
      data.setMailVerdict(id, verdict).then(
        () => {
          wrote = true
          if (gone) invalidate('mail')
        },
        (e) => {
          failure = { e }
          if (gone) rollback()
        },
      )
      const undo = () => {
        hideToast()
        if (before) qc.setQueryData(key, before)
        setMovedNow((m) => {
          const n = new Set(m)
          n.delete(id)
          return n
        })
        bringBack({ item, section, index })
        if (previous) data.setMailVerdict(id, previous).then(() => invalidate('mail'), (e) => fail(item.title, e))
      }
      exit(item, section, index, 'move', () => {
        gone = true
        if (failure) return rollback()
        patch((t) => patchMove(t, id, verdict))
        if (wrote) invalidate('mail')
        setMovedNow((m) => new Set(m).add(id))
        setFlash(verdict)
        later(DAY_WASH_MS, () => setFlash((f) => (f === verdict ? null : f)))
        showToast({ verb: `Moved to ${PILE_NAME[verdict]}`, title: item.title, icon: 'move', chips: { id, verdict }, undo })
      })
    },
    [busy, snapshot, invalidate, qc, key, bringBack, fail, hideToast, exit, patch, later, showToast],
  )

  // ── Snooze (mail only — the API has no snooze for reminders or assignments) ──
  const snooze = useCallback(
    (item: DayItem, section: Section, index: number, until: string, phrase: string) => {
      const mail = item.mail
      if (!mail || busy(item.key)) return
      setMenu(null)
      const id = mail.id
      const before = snapshot()
      let gone = false
      let wrote = false
      let failure: { e: unknown } | null = null
      const rollback = () => {
        if (before) qc.setQueryData(key, before)
        bringBack({ item, section, index })
        fail(item.title, failure?.e)
      }
      data.setMailState(id, 'snoozed', until).then(
        () => {
          wrote = true
          if (gone) invalidate('mail')
        },
        (e) => {
          failure = { e }
          if (gone) rollback()
        },
      )
      const undo = () => {
        hideToast()
        if (before) qc.setQueryData(key, before)
        bringBack({ item, section, index })
        data.setMailState(id, 'open').then(() => invalidate('mail'), (e) => fail(item.title, e))
      }
      exit(item, section, index, 'move', () => {
        gone = true
        if (failure) return rollback()
        patch((t) => patchSnooze(t, id, until))
        if (wrote) invalidate('mail')
        setFlash('snoozed')
        later(DAY_WASH_MS, () => setFlash((f) => (f === 'snoozed' ? null : f)))
        showToast({ verb: `Snoozed until ${phrase}`, title: item.title, icon: 'snooze', chips: null, undo })
      })
    },
    [busy, snapshot, invalidate, qc, key, bringBack, fail, hideToast, exit, patch, later, showToast],
  )

  // ── Next steps (Lane 6b) ──
  //
  // Optimistic like the pile moves: the row's status changes on the click, the call goes out, and
  // the triage (plus the calendar and reminders an accept can write) is re-read when it lands. A
  // refusal puts the row back and says so in the page's error toast. An Undo waits for the accept
  // it reverses, so a quick Undo can never reach the Worker before the accept did.

  const toggleSteps = useCallback((k: string) => setSteps((cur) => (cur === k ? null : k)), [])

  const invalidateProposal = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['mailTriage'] })
    void qc.invalidateQueries({ queryKey: ['mailAudit'] })
    void qc.invalidateQueries({ queryKey: ['calendar'] })
    void qc.invalidateQueries({ queryKey: ['tasks'] })
    void qc.invalidateQueries({ queryKey: ['reminders'] })
  }, [qc])

  const skipProposal = useCallback(
    (p: MailProposal) => {
      if (p.status !== 'proposed') return
      patch((t) => patchProposal(t, p.id, { status: 'skipped' }))
      data.skipMailProposal(p.id).then(invalidateProposal, (e) => {
        patch((t) => patchProposal(t, p.id, { status: p.status, skippedAt: p.skippedAt }))
        fail(p.title, e)
      })
    },
    [patch, invalidateProposal, fail],
  )

  const acceptProposal = useCallback(
    (item: DayItem, section: Section, index: number, p: MailProposal) => {
      const mail = item.mail
      if (!mail || p.status !== 'proposed') return
      const plan = acceptPlan(p)
      const revert = () => patch((t) => patchProposal(t, p.id, { status: p.status, acceptedAt: p.acceptedAt, result: p.result }))
      const what = 'title' in p.payload ? p.payload.title : p.title

      // mark_done: the card leaves its pile the way Done does, on the same beats and the same toast.
      if (plan.leaves) {
        if (busy(item.key)) return
        setMenu(null)
        setSteps(null)
        const before = snapshot()
        let gone = false
        let wrote = false
        let failure: { e: unknown } | null = null
        const rollback = () => {
          if (before) qc.setQueryData(key, before)
          setHandled((h) => h.filter((r) => r.key !== item.key))
          bringBack({ item, section, index })
          fail(item.title, failure?.e)
        }
        const accepted = data.acceptMailProposal(p.id)
        accepted.then(
          () => {
            wrote = true
            if (gone) invalidateProposal()
          },
          (e) => {
            failure = { e }
            if (gone) rollback()
          },
        )
        const undo = () => {
          hideToast()
          if (before) qc.setQueryData(key, before)
          setHandled((h) => h.filter((r) => r.key !== item.key))
          bringBack({ item, section, index })
          accepted.then(() => data.undoMailProposal(p.id)).then(invalidateProposal, (e) => fail(item.title, e))
        }
        exit(item, section, index, 'done', () => {
          gone = true
          if (failure) return rollback()
          patch((t) => patchHandled(patchProposal(t, p.id, { status: 'accepted' }), mail.id))
          if (wrote) invalidateProposal()
          setHandled((h) => [{ key: item.key, verb: 'Done', title: item.title, at: String(clockMinutes()), undo }, ...h.filter((r) => r.key !== item.key)])
          showToast({ verb: plan.toast ?? 'Done', title: item.title, icon: 'done', chips: null, undo })
        })
        return
      }

      const send = () => {
        patch((t) => patchProposal(t, p.id, { status: 'accepted' }))
        const accepted = data.acceptMailProposal(p.id)
        accepted.then(invalidateProposal, (e) => {
          revert()
          fail(p.title, e)
        })
        return accepted
      }

      // open_link: opened inside the click, so no popup blocker stands between the owner and it.
      if (plan.open) window.open(plan.open, '_blank', 'noopener')

      // draft_reply: copied first; the accept goes out only once the clipboard took it.
      if (plan.copy !== null) {
        const write = navigator.clipboard?.writeText ? navigator.clipboard.writeText(plan.copy) : Promise.reject(undefined)
        write.then(() => void send(), (e) => fail(p.title, e))
        return
      }

      const accepted = send()
      if (plan.toast && plan.undo) {
        const undo = () => {
          hideToast()
          revert()
          accepted.then(() => data.undoMailProposal(p.id)).then(invalidateProposal, (e) => fail(p.title, e))
        }
        showToast({ verb: plan.toast, title: what, icon: 'done', chips: null, undo })
      }
    },
    [patch, busy, snapshot, qc, key, bringBack, fail, invalidateProposal, hideToast, exit, clockMinutes, showToast],
  )

  /** A reason chip: a second verdict call with `reason`, and the toast says "noted · deadline". */
  const pickReason = useCallback(
    (reason: MailCorrectionReason) => {
      const t = toast
      if (!t?.chips) return
      const label = REASON_CHIPS.find((c) => c.reason === reason)?.label ?? reason
      setToast((cur) => (cur && cur.id === t.id ? { ...cur, noted: label } : cur))
      data.setMailVerdict(t.chips.id, t.chips.verdict, reason).catch((e) => fail(t.title, e))
    },
    [toast, fail],
  )

  const undo = useCallback(() => {
    const t = toast
    if (t?.undo && t.on) t.undo()
  }, [toast])

  /** Rows currently leaving a section after the data no longer lists them. */
  const ghosts = useCallback(
    (section: Section) =>
      Object.values(phases)
        .filter((p) => p.section === section && (p.phase === 'gone' || p.phase === 'leaving'))
        .map((p) => ({ item: p.item, index: p.index })),
    [phases],
  )

  const phaseOf = useCallback(
    (k: string, section: Section): { phase: Phase; mark?: 'done' | 'dismiss' } => {
      const p = phases[k]
      if (!p || p.section !== section) return { phase: 'rest' }
      return { phase: p.phase, mark: p.mark }
    },
    [phases],
  )

  return {
    phaseOf,
    ghosts,
    hidden,
    finish,
    move,
    snooze,
    toast,
    undo,
    hideToast,
    pickReason,
    handled,
    flash,
    menu,
    setMenu,
    movedNow,
    steps,
    toggleSteps,
    acceptProposal,
    skipProposal,
  }
}

export type DayController = ReturnType<typeof useDayController>
